<script setup>
import { onMounted, ref } from 'vue'

const isVisible = ref(false)

onMounted(() => {
  isVisible.value = true
})

const coreValues = [
  {
    title: '利他',
    desc: '以利他之心驱动决策，致力于为客户、合作伙伴及社会创造真实价值。正如水润万物，我们先赋能他人，再成就自我。',
    icon: '💧'
  },
  {
    title: '不争',
    desc: '如水之柔，顺势而为。不与客户争利，不与同行恶性竞争，避开无效内耗。',
    icon: '🌊'
  },
  {
    title: '处下',
    desc: '保持谦顺与敬畏，深潜于行业最底层，通过持续优化品质与服务，像大地一样承载并支撑客户的成功。',
    icon: '🗻'
  }
]
</script>

<template>
  <div class="about-page">
    <!-- Hero Section -->
    <section class="about-hero" :class="{ 'animate-fade': isVisible }">
      <div class="hero-overlay"></div>
      <div class="container hero-content">
        <h1>关于水芯</h1>
        <!-- <p class="hero-tagline">赋能未来 &middot; 驱动创新 &middot; 智造卓越</p> -->
      </div>
    </section>

    <!-- Company Identity (Vision & Mission) -->
    <section class="section identity-section">
      <div class="container">
        <div class="identity-grid">
          <div class="identity-card vision-card">
            <span class="section-label">企业愿景</span>
            <h2>成为最让客户信赖的芯片企业</h2>
            <div class="vision-tags">
              <span>品质</span>
              <span class="dot">·</span>
              <span>价格</span>
              <span class="dot">·</span>
              <span>服务</span>
            </div>
          </div>
          <div class="identity-card mission-card">
            <span class="section-label">企业使命</span>
            <div class="mission-list">
              <div class="mission-item">
                <span class="marker"></span>
                <p>让客户采购更简单、高效、透明</p>
              </div>
              <div class="mission-item">
                <span class="marker"></span>
                <p>让业务合伙人在回款后获得佣金提成</p>
              </div>
              <div class="mission-item">
                <span class="marker"></span>
                <p>让员工过上丰盛、喜悦的生活</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Core Values -->
    <section class="section values-section bg-light">
      <div class="container">
        <div class="section-header">
          <span class="section-label">核心价值观</span>
          <h2>水芯灵魂：利他 · 不争 · 处下</h2>
        </div>
        <div class="values-grid">
          <div v-for="value in coreValues" :key="value.title" class="value-card">
            <div class="value-icon">{{ value.icon }}</div>
            <h3>{{ value.title }}</h3>
            <p>{{ value.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="section cta-section">
      <div class="container cta-box">
        <h2>水利万物，芯系未来</h2>
        <p>诚邀全体合作伙伴与优秀人才，与水芯共同开启无限可能。</p>
        <div class="cta-btns">
          <!-- <a href="mailto:hr@water-core.com" class="btn btn-white">投递简历</a> -->
          <router-link to="/applications" class="btn btn-outline-white">探索应用</router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
.about-page {
  overflow-x: hidden;
}

.section {
  padding: 100px 0;
}

.section-label {
  display: block;
  color: #0088ff;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;
  font-size: 13px;
  margin-bottom: 20px;
}

.section-header {
  text-align: center;
  margin-bottom: 60px;
}

.section-header h2 {
  font-size: 32px;
  color: #1a1a1a;
}

.bg-light {
  background-color: #f8faff;
}

/* Hero Section */
.about-hero {
  position: relative;
  height: 40vh;
  min-height: 350px;
  background: url('@/assets/images/about-hero.png') center/cover;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #ffffff;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to right, rgba(0, 0, 0, 0.7), rgba(0, 88, 184, 0.3));
}

.hero-content {
  position: relative;
  z-index: 1;
}

.hero-content h1 {
  font-size: 56px;
  font-weight: 800;
  margin-bottom: 16px;
  letter-spacing: -1px;
}

.hero-tagline {
  font-size: 20px;
  opacity: 0.9;
  letter-spacing: 4px;
}

/* Identity Section */
.identity-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
}

.identity-card {
  background: white;
  padding: 50px;
  border-radius: 24px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.03);
  border: 1px solid #f1f5f9;
}

.vision-card h2 {
  font-size: 36px;
  color: #1a1a1a;
  line-height: 1.3;
  margin-bottom: 24px;
}

.vision-tags {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 20px;
  color: #0088ff;
  font-weight: 600;
}

.vision-tags .dot {
  opacity: 0.3;
}

.mission-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.mission-item {
  display: flex;
  align-items: flex-start;
  gap: 15px;
}

.mission-item .marker {
  width: 8px;
  height: 8px;
  background: #0088ff;
  border-radius: 50%;
  margin-top: 8px;
  flex-shrink: 0;
}

.mission-item p {
  font-size: 18px;
  color: #334155;
  margin: 0;
  line-height: 1.5;
  font-weight: 500;
}

/* Core Values */
.values-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
}

.value-card {
  background: white;
  padding: 40px;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 88, 184, 0.05);
  transition: transform 0.3s ease;
  border: 1px solid #f1f5f9;
  text-align: center;
}

.value-card:hover {
  transform: translateY(-10px);
  border-color: #0088ff;
}

.value-icon {
  font-size: 40px;
  margin-bottom: 24px;
}

.value-card h3 {
  font-size: 24px;
  margin-bottom: 16px;
  color: #1e293b;
}

.value-card p {
  color: #64748b;
  font-size: 16px;
  line-height: 1.7;
}

/* CTA Box */
.cta-box {
  background: linear-gradient(135deg, #005eb8, #0088ff);
  border-radius: 30px;
  padding: 80px 40px;
  text-align: center;
  color: white;
  box-shadow: 0 20px 50px rgba(0, 88, 184, 0.3);
}

.cta-box h2 {
  font-size: 40px;
  margin-bottom: 16px;
}

.cta-box p {
  font-size: 18px;
  opacity: 0.9;
  margin-bottom: 40px;
}

.cta-btns {
  display: flex;
  justify-content: center;
  gap: 20px;
}

.btn {
  padding: 14px 40px;
  border-radius: 50px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s;
}

.btn-white {
  background: white;
  color: #005eb8;
}

.btn-white:hover {
  background: #f1f5f9;
  transform: scale(1.05);
}

.btn-outline-white {
  border: 2px solid white;
  color: white;
}

.btn-outline-white:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* Animations */
.animate-fade {
  animation: fadeIn 1s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Responsive */
@media (max-width: 1024px) {
  .identity-grid {
    grid-template-columns: 1fr;
    gap: 30px;
  }
}

@media (max-width: 640px) {
  .hero-content h1 { font-size: 40px; }
  .section { padding: 60px 0; }
  .cta-btns { flex-direction: column; }
}
</style>
